import { useCallback, useMemo, useState } from 'react'
import { useHydrated } from 'react-hydration-provider'
import Alert from 'components/Alert'
import Loading from 'components/Loading'
import InfiniteResults from 'components/Dashboard/InfiniteResults'
import InfiniteSales from 'components/Dashboard/InfiniteSales'
import InfiniteOffers from 'components/Dashboard/InfiniteOffers'
import DashboardMain from 'components/Dashboard/Main'
import DashboardPage from 'components/Dashboard/Page'
import ManageHeader from './Header'
import useManageDashboard from './ManageDashboard.hooks'
import { queryTypes, useQueryState } from 'hooks/useHydratedQueryState'
import { useManageViewStore } from 'store/index'
import { classNames } from 'helpers/utilities'
import { ResultsSize } from 'components/Dashboard/Dashboard.types'
import { ManageDashboardTabId, ManageDashboardView } from './ManageDashboard.types'

export default function ManageDashboard() {
  const [view] = useQueryState(
    'view',
    queryTypes
      .stringEnum<ManageDashboardView>(Object.values(ManageDashboardView))
      .withDefault(ManageDashboardView.owned)
  )

  const [search] = useQueryState('search', queryTypes.string.withDefault(''))

  const viewSetting = useManageViewStore((state) => state.view)
  const setViewSetting = useManageViewStore((state) => state.setView)

  const [resultsSize, setResultsSize] = useState<ResultsSize>({
    width: 0,
    height: 0
  })

  const { queries, counts, usersQuery, activeWallet } = useManageDashboard({
    resultsSize,
    viewSetting,
    current: view,
    search
  })

  const hydrated = useHydrated()

  const tabs = useMemo(() => {
    const labels = {
      owned: 'Owned',
      expirations: 'Expirations',
      forSale: 'For Sale',
      reserved: 'Reserved',
      sales: 'Sales',
      offers: 'Offers'
    }

    return Object.values(ManageDashboardView).map((tabId) => {
      return {
        id: tabId,
        name: labels[tabId],
        href: `/manage?view=${tabId}`,
        count: counts[tabId],
        isCurrent: view === tabId,
        isDisabled: !['owned', 'offers'].includes(tabId) && counts[tabId] === 0
      }
    })
  }, [counts, view])

  const resultsRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      const {
        width: compWidth,
        height: compHeight,
        paddingLeft,
        paddingRight,
        paddingTop,
        paddingBottom
      } = getComputedStyle(node)

      const width = parseInt(compWidth) - parseInt(paddingLeft) - parseInt(paddingRight)

      const height = parseInt(compHeight) - parseInt(paddingTop) - parseInt(paddingBottom)

      setResultsSize({ width, height })
    }
  }, [])

  const isList = viewSetting === 'list'
  const isSales = view === ManageDashboardView.sales
  const paddingTopClassName = isSales || isList ? 'sm:py-6' : 'py-6'

  const renderView = () => {
    switch (view) {
      case ManageDashboardView.owned:
        return (
          <InfiniteResults
            query={queries.owned.query}
            viewSetting={viewSetting}
            activeWallet={activeWallet}
            search={search}
            searchPlaceholder="Search owned NFDs"
            viewId={ManageDashboardView.owned}
            context="manage"
          />
        )

      case ManageDashboardView.expirations:
        return (
          <InfiniteResults
            query={queries.expirations.query}
            viewSetting={viewSetting}
            activeWallet={activeWallet}
            search={search}
            searchPlaceholder="Search NFD expirations"
            viewId={ManageDashboardView.expirations}
            context="manage"
          />
        )

      case ManageDashboardView.forSale:
        return (
          <InfiniteResults
            query={queries.forSale.query}
            viewSetting={viewSetting}
            activeWallet={activeWallet}
            search={search}
            searchPlaceholder="Search for sale NFDs"
            viewId={ManageDashboardView.forSale}
            context="manage"
          />
        )

      case ManageDashboardView.reserved:
        return (
          <InfiniteResults
            query={queries.reserved.query}
            viewSetting={viewSetting}
            activeWallet={activeWallet}
            search={search}
            searchPlaceholder="Search reserved NFDs"
            viewId={ManageDashboardView.reserved}
            context="manage"
          />
        )

      case ManageDashboardView.sales:
        return (
          <InfiniteSales
            query={queries.sales.query}
            usersQuery={usersQuery}
            totalSales={counts.sales}
          />
        )

      case ManageDashboardView.offers:
        return (
          <InfiniteOffers
            sentQuery={queries.offersSent.query}
            receivedQuery={queries.offersReceived.query}
            usersQuery={usersQuery}
          />
        )

      default:
        return null
    }
  }

  if (!hydrated) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <Loading />
      </div>
    )
  }

  if (!activeWallet) {
    return (
      <div className="px-6 py-5">
        <Alert
          type="warning"
          title="Wallet not connected"
          message="Please connect to an Algorand wallet using the button above."
        />
      </div>
    )
  }

  return (
    <DashboardPage<ManageDashboardTabId>
      headerComp={
        <ManageHeader
          title="Manage Account"
          address={activeWallet}
          current={view}
          viewSetting={viewSetting}
          setViewSetting={setViewSetting}
          hideViewToggle={['sales', 'offers']}
        />
      }
      tabs={tabs}
      current={view}
      viewSetting={viewSetting}
      setViewSetting={setViewSetting}
      hideViewToggle={['sales', 'offers']}
    >
      <DashboardMain
        ref={resultsRef}
        className={classNames(paddingTopClassName, 'px-4 sm:px-6 lg:px-8')}
      >
        {renderView()}
      </DashboardMain>
    </DashboardPage>
  )
}
